import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAppStore } from '@/app/store';
import {
  formatTimeDisplay,
  getDateFnsLocale,
  parseDateKey,
} from '@/domain/dates';
import { computeFreeIntervals, filterPlans } from '@/domain/plans';
import { partnerOf } from '@/domain/helpers';
import { PlanChip } from './PlanChip';
import { QuickAdd } from './QuickAdd';
import { DayMediaSection } from './DayMediaSection';
import styles from './DayPanel.module.css';

export function DayPanel() {
  const { t, i18n } = useTranslation();
  const session = useAppStore((s) => s.session);
  const profiles = useAppStore((s) => s.profiles);
  const plans = useAppStore((s) => s.plans);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);
  const selectedDate = useAppStore((s) => s.selectedDate);
  const openPlanEditor = useAppStore((s) => s.openPlanEditor);

  const locale = preferences?.locale ?? (i18n.language.startsWith('ru') ? 'ru' : 'en');
  const timeFormat = preferences?.timeFormat ?? '24h';
  const viewerId = session?.user.id ?? '';
  const partner = session ? partnerOf(profiles, session.user.id) : null;

  const dayPlans = useMemo(() => {
    if (!selectedDate || !viewerId) return [];
    return filterPlans(
      plans.filter((p) => !p.deletedAt && p.planDate === selectedDate),
      filters,
      viewerId,
    );
  }, [plans, filters, viewerId, selectedDate]);

  const myPlans = dayPlans.filter((p) => p.ownerId === viewerId);
  const partnerPlans = dayPlans.filter((p) => p.ownerId !== viewerId);

  const freeIntervals = useMemo(() => {
    if (!selectedDate) return [];
    const timed = dayPlans.filter((p) => p.status === 'active');
    return computeFreeIntervals(timed);
  }, [dayPlans, selectedDate]);

  if (!selectedDate) {
    return (
      <div className={styles.panel}>
        <p className={styles.emptyHint}>{t('app.selectDay')}</p>
      </div>
    );
  }

  const dateLabel = format(parseDateKey(selectedDate), 'EEEE, d MMM', {
    locale: getDateFnsLocale(locale),
  });
  const dateFull = format(parseDateKey(selectedDate), 'EEEE, d MMMM yyyy', {
    locale: getDateFnsLocale(locale),
  });

  const renderPlanList = (items: typeof dayPlans) => (
    <div className={styles.planList}>
      {items.map((plan) => (
        <PlanChip key={plan.id} plan={plan} />
      ))}
    </div>
  );

  return (
    <div className={styles.panel}>
      <div>
        <h2 className={styles.dateHeading}>{dateLabel}</h2>
        <p className={styles.dateSub}>{dateFull}</p>
      </div>

      <div className={styles.actions}>
        <QuickAdd date={selectedDate} />
        <Button variant="secondary" size="sm" onClick={() => openPlanEditor()}>
          {t('app.newPlan')}
        </Button>
      </div>

      <DayMediaSection dateKey={selectedDate} />

      {dayPlans.length === 0 ? (
        <EmptyState title={t('common.empty')} />
      ) : (
        <>
          {myPlans.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('app.myPlans')}</h3>
              {renderPlanList(myPlans)}
            </section>
          )}

          {partnerPlans.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {partner?.displayName ?? t('app.partnerPlans')}
              </h3>
              {renderPlanList(partnerPlans)}
            </section>
          )}
        </>
      )}

      {freeIntervals.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('app.freeSlots')}</h3>
          <ul className={styles.freeList}>
            {freeIntervals.map((slot) => (
              <li key={`${slot.start}-${slot.end}`} className={styles.freeItem}>
                {formatTimeDisplay(slot.start, timeFormat)} – {formatTimeDisplay(slot.end, timeFormat)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
